"use strict";
/*
 * there was a reason i made these instead of using lodash/fp but i forget what it was. i think maybe
 * these do more validation that the second function gets a compatible object.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.tuple = exports.roundToPrecision = exports.memoize = exports.once = exports.mapFind = exports.fnIf = exports.retryWithDelay = exports.flow = exports.coerceArray = exports.putKeyValue = exports.getKeyValueOr = exports.getKeyValue = void 0;
/**
 * Returns a function that gets the value of the given key from an object
 *
 * @param key
 * @example
 * const getAuthor = getKeyValue('author');
 * const author = getAuthor(book);
 */
const getKeyValue = (key) => (obj) => obj[key];
exports.getKeyValue = getKeyValue;
/**
 * Returns a function that gets the value of the given key from an object, or the given default
 * value if the key is not present.
 *
 * @param key
 * @param defaultValue a default value matching the type of the value at the given key
 * @example
 * const getAuthorOrNope = getKeyValueOr('author', 'nope');
 * const authorOrNope = getAuthorOrNope(book);
 */
const getKeyValueOr = (key, defaultValue) => (obj) => obj[key] || defaultValue;
exports.getKeyValueOr = getKeyValueOr;
/**
 * Returns a function that sets the value of the given key on an object.
 *
 * @param key
 * @example
 * const putAuthor = putKeyValue('author');
 * const newBook = putAuthor(book, 'tom');
 */
const putKeyValue = (key) => (obj, value) => ({ ...obj, [key]: value });
exports.putKeyValue = putKeyValue;
/**
 * Coerces a value into an array. If the value is already an array, it is returned as-is.
 * If the value is undefined, an empty array is returned.
 *
 * @param thing the value
 * @returns an array
 */
const coerceArray = (thing) => thing instanceof Array ? thing : thing !== undefined ? [thing] : [];
exports.coerceArray = coerceArray;
/**
 * this is like lodash/flow but it uses a recursive type instead of hard-coding parameters
 */
const flow = (...chain) => (param) => {
    let result = param;
    for (const fn of chain) {
        result = fn(result);
    }
    return result;
};
exports.flow = flow;
/**
 * Retries a function with a delay between retries.
 * @param fn The function to retry
 * @param [options] retry options
 * @param [options.wait=100] base wait of first retry (number), further retries are: t=n*wait,
 * default: 100
 * @param [options.splay=0.5] percentage to modify t by. 0.5 is 50%, which would make
 * t=n*wait+rand(wait*splay*-1, wait*splay), default: 0.5
 * @param [options.retries=2] number of times to retry. (2 retries means it'll try a max of 3 times),
 * default: 2
 * @param [options.n=0] the starting retry index. probably don't set this. unless you really want a very steep
 *  initial wait cliff with incremental increases, then maybe you'd set this. default: 0
 * @returns A promise that resolves with the result of the function, or rejects with the last error.
 */
const retryWithDelay = (fn, options) => {
    const { wait, splay, retries, n } = { wait: 100, splay: 0.5, retries: 2, n: 0, ...options };
    if (n >= retries) {
        return fn();
    }
    const timeout = (n + 1) * wait + (Math.random() * 2 - 1) * splay * wait;
    const retry = () => new Promise((resolve, reject) => {
        setTimeout(() => (0, exports.retryWithDelay)(fn, { ...options, n: n + 1 }).then(resolve, reject), timeout);
    });
    return fn().catch(retry);
};
exports.retryWithDelay = retryWithDelay;
/**
 * a shameful helper to avoid needing to test code coverage of branches
 */
const fnIf = (condition, trueValue, falseValue) => condition ? trueValue : falseValue;
exports.fnIf = fnIf;
/**
 * maps the array and returns the first result that matches the predicate
 * avoids processing extra elements that would happen with .map().find() or .reduce
 *
 * eg the third element of the array is never processed:
 *   const result = mapFind([1,2,3], x => 'hello'.charAt(x), x => x === 'l');
 */
const mapFind = (array, mapper, predicate = (r) => !!r) => {
    for (const item of array) {
        const mapped = mapper(item);
        if (predicate(mapped)) {
            return mapped;
        }
    }
};
exports.mapFind = mapFind;
/**
 * returns a function that will only ever call the given function once, returning the first result for every subsequent call
 *
 * eg:
 *   const heavyFunction = () => 'hello';
 *   const butOnlyOnce = once(() => 'hello');
 *
 *   heavyFunction() // returns `hello`;
 *   butOnlyOnce() // returns `hello`;
 */
const once = (fn) => {
    const initialValue = {};
    let result = initialValue;
    return ((...args) => result === initialValue ? (result = fn(...args)) : result);
};
exports.once = once;
/**
 * memoizes a function with any number of arguments
 */
const memoize = (fn) => {
    const cache = {};
    const resolveCache = (cacheLayer, [first, ...rest]) => {
        if (!first) {
            return cacheLayer;
        }
        const layers = first instanceof Object
            ? (cacheLayer.weakLayers = (cacheLayer.weakLayers || (typeof WeakMap === 'undefined' ? undefined : new WeakMap())))
            : (cacheLayer.strongLayers = (cacheLayer.strongLayers || new Map()));
        // argument is an object and WeakMap is not supported
        if (!layers) {
            return {};
        }
        const layer = layers.get(first) || {};
        if (!layers.has(first)) {
            layers.set(first, layer);
        }
        return resolveCache(layer, rest);
    };
    return ((...args) => {
        const thisCache = resolveCache(cache, args);
        return thisCache.result = (thisCache.result || fn(...args));
    });
};
exports.memoize = memoize;
/**
 * rounds number to given number of places
 *
 * eg:
 *  roundToPrecision(1234.123, 2); // returns 1200
 *  roundToPrecision(1234.123, -2); // returns 1234.12
 *  roundToPrecision(1234.125, -2); // returns 1234.13
 */
const roundToPrecision = (num, places) => {
    const multiplier = Math.pow(10, -1 * places);
    // https://stackoverflow.com/a/11832950/14809536
    return Math.round((num + Number.EPSILON) * multiplier) / multiplier;
};
exports.roundToPrecision = roundToPrecision;
/**
 * a silly utility to help typescript realize an array is a tuple
 *
 * eg:
 *  const a = [5, 'string'] // type is `Array<string | number>`
 *  const t = tuple(5, 'string') type is `[5, 'string']`
 *
 * both have the same javascript value, but one is forced to be a tuple, which
 * is nice if its structure is important. examples are like the React.useState
 * pattern where there are two return values in a tuple, or if you're feeding
 * Object.fromEntries
 *
 */
const tuple = (...args) => args;
exports.tuple = tuple;
